import React, { useMemo } from 'react';
import { shallowEqual, useSelector } from 'umi';
import { useRequest } from 'ahooks';
import { useCaseInsensitiveParams, useSearchParamsAppoint } from '@/pages/aigc/hooks/useQuery';
import { getSnapshotData } from '../service/service';
import { v4 as uuidv4 } from 'uuid';
import { useErrorTips } from '@/pages/aigc/hooks/useErrorTips';
import { hasDocumentType, hasCosExcelType } from './utils';
import { ChatShareConversationPage } from './components/ChatShareConversationPage';
import { ChatShareConversationPageWithDsReport } from './components/ChatShareConversationPageWithDsReport';
import { ChatContentType } from '../ChatDSTemplate/type';
import './index.scss';

const ChatShareConversationContainer = () => {
  const { snapshotId, poolId, shareId, msgid, template } = useCaseInsensitiveParams(useSearchParamsAppoint(), ["snapshotId", "shareId", 'poolId', 'msgid', 'template']);
  const showErrorTips = useErrorTips();
  const userInfo = useSelector(
    (state: any) => state.home.userInfo,
    shallowEqual,
  );
  const { data } = useRequest(
    () =>
      getSnapshotData(
        {
          traceId: uuidv4(),
          snapshotId: snapshotId as string,
          isPool: !!poolId || !!msgid,
          sessionId: poolId as string,
          user_id: userInfo?.userid,
          message_id: msgid as string,
        },
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        showErrorTips,
      ),
    {
      ready: !!snapshotId || !!poolId || !!msgid,
    },
  );

  // 通过数据内容判断是否包含 ds 报告（兜底逻辑）
  const hasDocument = useMemo(() => hasDocumentType(data?.messages || []), [data]);
  // 判断是否包含魔法海报
  const isMagicPoster = useMemo(
    () => data?.data?.function?.name === 'magic_poster',
    [data],
  );
  // 判断是否包含 AI 智能表格
  const isExcelInitLayout = useMemo(
    () => data?.data?.function?.name === 'ai_excel',
    [data],
  );
  // 判断是否包含正文 <cos> 标签返回的 excel（xlsx/xls），应识别为智能表格只读预览
  const hasCosExcel = useMemo(() => hasCosExcelType(data?.messages || []), [data]);

  // 优先通过 URL 中的 template 参数判断布局类型，兜底通过数据内容判断
  const contentType = useMemo(() => {
    if (isMagicPoster || template === 'magicPoster') return ChatContentType.MagicPoster;
    // cos 标签 xlsx 与真正表格（aiExcel 模板）一样渲染智能表格面板（分享场景为只读预览）
    if (isExcelInitLayout || template === 'aiExcel' || hasCosExcel) return ChatContentType.SmartTable;
    // splitView：普通对话里点 HTML 卡片切入的 AI Gallery 预览；skill 同用 AiGallery 面板
    if (template === 'splitView' || template === 'skill') return ChatContentType.AiGallery;
    if (hasDocument || template === 'dsReport') return ChatContentType.Document;
    return null;
  }, [template, isMagicPoster, isExcelInitLayout, hasDocument, hasCosExcel]);
  if (contentType) {
    return <ChatShareConversationPageWithDsReport {...{ data, snapshotId, poolId, sessionId: shareId, contentType }} />;
  }

  return (
    <ChatShareConversationPage {...{ data, snapshotId, poolId }} />
  );
};

export default ChatShareConversationContainer;